import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  updateCurrencyRateDetails,
  updateFormData,
} from "../../../../../store/slices/currency/currencyRateFormSlice";
import { useEffect } from "react";
import { useGetBasicDataQuery } from "../../../../../store/queries/global";

const useSell = ({ refetch, closeModal }) => {
  const dispatch = useDispatch();

  const { selectedItemsDetails, selectedId } = useSelector(
    (state) => state.currencyRateForm
  );
  const { data: formData = {} } = useGetBasicDataQuery();

  const validation = Yup.object({
    markup_value: Yup.number().when("markup_type", {
      is: 2,
      then: (schema) =>
        schema
          .min(0, "Value must be at least 0")
          .max(100, "Value must be at most 100"),
    }),
  });

  const formik = useFormik({
    initialValues: {
      cash_rate:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.rate
          : "",
      markup_type:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.markup_type
          : "",
      markup_value:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.markup_value
          : "",
      cash_forex_rate:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.total_amount
          : "",
      cash_status:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.status !== null
            ? selectedItemsDetails?.sell_details?.Cash?.status
            : 0
          : 0,
      cash_lock:
        selectedItemsDetails !== ""
          ? (selectedItemsDetails?.sell_details?.Cash?.locked ??
            selectedItemsDetails?.sell_details?.Cash?.lock)
            ? 1
            : 0
          : 0,
      cash_is_all_branches:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.is_all_branches ?? 1
          : 1,
      cash_branch_ids:
        selectedItemsDetails !== ""
          ? selectedItemsDetails?.sell_details?.Cash?.branch_ids ?? []
          : [],
    },

    validationSchema: validation,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      let obj = {
        // NOTE: identifier key for the currency rate update payload.
        currency_id: selectedId,
        type: 2,
        product: [
          {
            id: selectedItemsDetails?.sell_details?.Cash?.id,
            markup_type: values?.markup_type,
            markup_value: +values?.markup_value ?? 0,
            rate: values?.cash_rate,
            total_amount: values.cash_forex_rate,
            status: +values?.cash_status ?? 0,
            lock: +values?.cash_lock ?? 0,
            is_all_branches: +values?.cash_is_all_branches ?? 0,
            ...(+values?.cash_is_all_branches
              ? {}
              : {
                  branch_ids: (values?.cash_branch_ids || []).map(
                    (v) => v?._id ?? v
                  ),
                }),
          },
        ],
      };

      dispatch(updateCurrencyRateDetails(obj)).then((response) => {
        if (response?.payload?.data?.success) {
          resetForm();
          closeModal?.();
          refetch();
          dispatch(
            updateFormData((state) => {
              state.showRateEditModal = false;
            })
          );

          toast.success(response?.payload?.data?.message);
        } else {
          // Rejected: payload is the error body { success, message, data }
          // where data holds field errors keyed as `product.<index>.<field>`.
          const handled = applyServerErrors(response?.payload?.data);
          if (!handled && response?.payload?.message) {
            toast.error(response?.payload?.message);
          }
        }
      });
    },
  });

  // Maps backend validation errors keyed as `product.<index>.<field>`
  // (e.g. "product.0.is_all_branches") onto the matching Sell formik field so
  // the message renders inline. Sell has a single Cash product (index 0).
  const applyServerErrors = (errors) => {
    if (!errors || typeof errors !== "object") return false;
    const fieldMap = {
      total_amount: "markup_value",
      markup_value: "markup_value",
      markup_type: "markup_type",
      rate: "cash_rate",
      is_all_branches: "cash_branch_ids",
      branch_ids: "cash_branch_ids",
      lock: "cash_lock",
      status: "cash_status",
    };
    let handled = false;
    Object.entries(errors).forEach(([key, messages]) => {
      const match = /^product\.(\d+)\.(.+)$/.exec(key);
      if (!match || Number(match[1]) !== 0) return;
      const fieldName = fieldMap[match[2]] ?? match[2];
      const message = Array.isArray(messages) ? messages[0] : messages;
      formik.setFieldError(fieldName, message);
      handled = true;
    });
    return handled;
  };

  const handleCloseModal = () => {
    dispatch(
      updateFormData((state) => {
        state.showRateEditModal = false;
      })
    );
  };

  const setTypeFieldValue = (id) => {
    if (formik.values?.markup_value) {
      handleChangeCashForex(formik.values?.markup_value, id);
    }
  };

  const handleChangeCashForex = (e, id) => {
    if (e.target) {
      formik.setFieldValue("markup_value", e.target.value);
    } else {
      formik.setFieldValue("markup_value", e);
    }
    const percentage = Number(e?.target?.value ?? formik?.values?.markup_value);
    const cashRate = Number(formik.values?.cash_rate) || 0;
    const cashMarkup = Math.abs(percentage) || 0;
    let rate = 0;

    if (id === 1) {
      const formattedRate = percentage.toFixed(4);
      formik.setFieldValue("cash_forex_rate", formattedRate);
    } else if (id === 2) {
      if (percentage > 0) {
        rate = cashRate + cashRate * (cashMarkup / 100);
      } else if (percentage < 0) {
        rate = cashRate - cashRate * (cashMarkup / 100);
      } else {
        rate = cashRate;
      }
      const formattedRate = rate.toFixed(4);
      formik.setFieldValue("cash_forex_rate", formattedRate);
    } else {
      rate = cashRate - percentage;
      const formattedRate = rate.toFixed(4);
      formik.setFieldValue("cash_forex_rate", formattedRate);
    }
  };

  // updating the completed percentage of butfields
  const sellFields = ["cash_markup"];

  var completedFields = sellFields?.filter((field) => {
    return !!!formik?.errors[field];
  });

  const completedPercentageSell =
    (completedFields.length / sellFields.length) * 100;

  useEffect(() => {
    // Dispatch the action to update the Redux state
    dispatch(
      updateFormData((state) => {
        state.CompletedPercentage = {
          ...state.CompletedPercentage,
          Sale: completedPercentageSell,
        };
      })
    );
    //eslint-disable-next-line
  }, [completedPercentageSell]);

  // Pre-calculate the (disabled) "Cash Forex Rate" field on initial load so it
  // is populated from rate + markup before the user touches the markup input.
  // Only runs when the forex rate is empty (e.g. the no-saved-data fallback),
  // so an existing total_amount from the edit API is left untouched.
  useEffect(() => {
    if (!formik.values?.cash_forex_rate && formik.values?.markup_type) {
      handleChangeCashForex(
        formik.values?.markup_value ?? "",
        formik.values?.markup_type
      );
    }
    // eslint-disable-next-line
  }, [selectedItemsDetails]);

  return {
    handleCloseModal,
    formik,
    handleChangeCashForex,
    formData,
    setTypeFieldValue,
  };
};

export default useSell;
